export const API_VERSION_PREFIX = '/api/v1';

const id = encodeURIComponent;

const HUB = `${API_VERSION_PREFIX}/brand_hub`;

/** The four CMYK channels that identify one proposed colour. */
export interface CmykShade {
  cyan: number;
  magenta: number;
  yellow: number;
  key: number;
}

const shade = (values: CmykShade) => new URLSearchParams(Object.entries(values).map(([channel, value]) => [channel, String(value)])).toString();

export const endpoints = {
  health: () => `${API_VERSION_PREFIX}/health`,

  auth: {
    session: () => `${API_VERSION_PREFIX}/auth/session`,
    selectOrganization: () => `${API_VERSION_PREFIX}/auth/session/organization`,
    // Admin only. Reports the groups an admin may grant, not what the caller holds.
    permissions: () => `${API_VERSION_PREFIX}/auth/permissions`,
  },

  dashboard: {
    summary: () => `${API_VERSION_PREFIX}/dashboard/summary`,
    attention: () => `${API_VERSION_PREFIX}/dashboard/attention`,
  },

  campaigns: {
    // Firestore serves the list. This path is served by the mocks alone, for a run without it.
    list: () => `${API_VERSION_PREFIX}/campaigns`,
    detail: (campaignId: string) => `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}`,
    // Firestore serves the market catalogue too. Mocks alone answer this path.
    markets: () => `${API_VERSION_PREFIX}/campaigns/-/markets`,
    extractions: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/extractions`,
    create: () => `${API_VERSION_PREFIX}/campaigns`,
    update: (campaignId: string) => `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}`,
    duplicate: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/duplicate`,
    deactivate: (campaignId: string) => `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}`,
    extractBriefing: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/briefing/extract`,
    seasonal: (startsOn: string, endsOn: string) =>
      `${API_VERSION_PREFIX}/campaigns/-/seasonal?${new URLSearchParams({ starts_on: startsOn, ends_on: endsOn }).toString()}`,
  },

  monitoring: {
    anomalies: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/anomalies`,
    resolveAnomaly: (campaignId: string, anomalyId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/anomalies/${id(anomalyId)}/resolve`,
    redZones: (campaignId: string) =>
      `${API_VERSION_PREFIX}/campaigns/${id(campaignId)}/red-zones`,
  },

  // A brand and its assets have a slug and an id. A product has a slug and a sku.
  // Only MSW gives `list` and `imports`. The hub reads these from Firestore.
  brandHub: {
    brands: () => `${HUB}/brands`,
    brand: {
      create: () => `${HUB}/brands`,
      update: (slug: string) => `${HUB}/brands/${id(slug)}`,
      colours: (slug: string) => `${HUB}/brands/${id(slug)}/colours`,
      extractColours: (slug: string) => `${HUB}/brands/${id(slug)}/colours/extract`,
      extractedColour: (slug: string, values: CmykShade) => `${HUB}/brands/${id(slug)}/colours/extracted?${shade(values)}`,
      fonts: (slug: string) => `${HUB}/brands/${id(slug)}/fonts`,
      font: (slug: string, fontId: string) => `${HUB}/brands/${id(slug)}/fonts/${id(fontId)}`,
      deactivate: (slug: string) => `${HUB}/brands/${id(slug)}`,
      assets: {
        list: (slug: string) => `${HUB}/brands/${id(slug)}/assets`,
        create: (slug: string) => `${HUB}/brands/${id(slug)}/assets`,
        update: (slug: string, assetId: string) =>
          `${HUB}/brands/${id(slug)}/assets/${id(assetId)}`,
        delete: (slug: string, assetId: string) =>
          `${HUB}/brands/${id(slug)}/assets/${id(assetId)}`,
      },
    },
    product: {
      list: (slug: string) => `${HUB}/brands/${id(slug)}/products`,
      create: (slug: string) => `${HUB}/brands/${id(slug)}/products`,
      update: (slug: string, productId: string) =>
        `${HUB}/brands/${id(slug)}/products/${id(productId)}`,
      delete: (slug: string, productId: string) =>
        `${HUB}/brands/${id(slug)}/products/${id(productId)}`,
      importCheck: (slug: string) => `${HUB}/brands/${id(slug)}/products/import/check`,
      import: (slug: string) => `${HUB}/brands/${id(slug)}/products/import`,
      imports: (slug: string) => `${HUB}/brands/${id(slug)}/products/imports`,
    },
    templates: () => `${HUB}/templates`,
    template: {
      inspect: () => `${HUB}/templates/inspect`,
      create: () => `${HUB}/templates`,
      update: (templateId: string) => `${HUB}/templates/${id(templateId)}`,
      delete: (templateId: string) => `${HUB}/templates/${id(templateId)}`,
      // What the editor has drawn. An uploaded archive has none until it is opened.
      canvas: (templateId: string) => `${HUB}/templates/${id(templateId)}/canvas`,
    },
    generatedAssets: () => `${HUB}/generated_assets`,
  },

  organization: {
    detail: () => `${API_VERSION_PREFIX}/organization`,
    update: () => `${API_VERSION_PREFIX}/organization`,
  },

  // Only MSW gives `list`. In a configured environment the app reads the members of
  // an organization from Firestore, by `active_organization_ids`.
  users: {
    list: () => `${API_VERSION_PREFIX}/users`,
    add: () => `${API_VERSION_PREFIX}/users`,
    update: (email: string) => `${API_VERSION_PREFIX}/users/${id(email)}`,
    revoke: (email: string) => `${API_VERSION_PREFIX}/users/${id(email)}`,
  },

  profile: {
    preferences: () => `${API_VERSION_PREFIX}/profile/preferences`,
  },
} as const;
